<template>
  <g
    v-if="lines.length > 0"
    class="net__connection"
    transform="translate(0,0)"
    :class="[
      {
        'is-selected': selected,
        'is-inverse': isSlopeNegative,
        'is-highlighted': highlighted,
        'is-faded': isFaded,
        'is-incoming': incoming,
        'is-outgoing': outgoing,
        'is-connecting': connecting,
        'is-zero': isZero,
        'is-non': non,
        'is-unclickable': !clickable
      },
      'net__connection_' + strength
    ]"
    @contextmenu.prevent="onContextMenu"
  >
    <template v-if="selected || incoming || outgoing || endpointsOnly == false">
      <!--
        The linebounds path is used to catch any clicks on the path. Since
        the main path might have holes in it (because of stroke-dasharray),
        we need a continous line to catch the clicks in these holes.
      -->
      <path
        class="net__connection__linebounds"
        :d="description"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="onPointerDown"
      />

      <!--
        stroke-dasharray below HAS to be written in the ternary way inside
        the style attribute. I tried moving it to a computed property, but
        the path wouldn't update correctly when changing from zero to
        standard.
      -->
      <path
        class="net__connection__line"
        :d="description"
        :style="{
          'stroke-dasharray': isZero ? (non ? '1 6' : '4') : `1 0`
        }"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="onPointerDown"
      />

      <g :transform="`translate(${end?.x}, ${end?.y})`">
        <polygon
          :points="arrowheadPoints"
          class="net__connection__arrow"
          @click.exact="select"
          @click.shift="addToSelection"
          @pointerdown="markAsHandled"
        />
      </g>

      <g
        v-show="isSlopeNegative"
        class="net__connection__inversemarker"
        :transform="`translate(${inverseMarkCenter?.x}, ${inverseMarkCenter?.y})`"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="markAsHandled"
      >
        <circle
          class="net__connection__inversemarker__circle"
          cx="0"
          cy="0"
          r="14"
        />
        <lucide-icon
          name="inverse"
          class="net__connection__inversemarker__icon"
          :height="selected ? 12 : 8"
          :width="selected ? 12 : 8"
          :x="selected ? -6 : -4"
          :y="selected ? -6 : -4"
        />
      </g>
      <g
        v-show="isSlopeNegative"
        class="net__connection__inversemarker"
        :transform="`translate(${inverseMarkCenter2?.x}, ${inverseMarkCenter2?.y})`"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="markAsHandled"
      >
        <circle
          class="net__connection__inversemarker__circle"
          cx="0"
          cy="0"
          r="14"
        />
        <lucide-icon
          name="inverse"
          class="net__connection__inversemarker__icon"
          :height="selected ? 12 : 8"
          :width="selected ? 12 : 8"
          :x="selected ? -6 : -4"
          :y="selected ? -6 : -4"
        />
      </g>
    </template>
    <template v-else>
      <!-- OUT -->
      <path
        class="net__connection__linebounds"
        :d="lineOutDescription"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="markAsHandled"
      />
      <path
        class="net__connection__line"
        :d="lineOutDescription"
        :style="{
          'stroke-dasharray': isZero ? '4' : `1 0`
        }"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="markAsHandled"
      />

      <!-- IN -->
      <path
        class="net__connection__linebounds"
        :d="lineInDescription"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="markAsHandled"
      />
      <path
        class="net__connection__line"
        :d="lineInDescription"
        :style="{
          'stroke-dasharray': isZero ? '4' : `1 0`
        }"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="markAsHandled"
      />

      <g :transform="`translate(${end?.x}, ${end?.y})`">
        <polygon
          :points="arrowheadPoints"
          class="net__connection__arrow"
          @click.exact="select"
          @click.shift="addToSelection"
          @pointerdown="markAsHandled"
        />
      </g>

      <g
        v-show="isSlopeNegative"
        class="net__connection__inversemarker"
        :transform="`translate(${lineOutEndPoint?.x}, ${lineOutEndPoint?.y})`"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="markAsHandled"
      >
        <circle
          class="net__connection__inversemarker__circle"
          cx="0"
          cy="0"
          r="14"
        />
        <lucide-icon
          name="inverse"
          class="net__connection__inversemarker__icon"
          :height="selected ? 12 : 8"
          :width="selected ? 12 : 8"
          :x="selected ? -6 : -4"
          :y="selected ? -6 : -4"
        />
      </g>
      <g
        v-show="isSlopeNegative"
        class="net__connection__inversemarker"
        :transform="`translate(${lineInStartPoint?.x}, ${lineInStartPoint?.y})`"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="markAsHandled"
      >
        <circle
          class="net__connection__inversemarker__circle"
          cx="0"
          cy="0"
          r="14"
        />
        <lucide-icon
          name="inverse"
          class="net__connection__inversemarker__icon"
          :height="selected ? 12 : 8"
          :width="selected ? 12 : 8"
          :x="selected ? -6 : -4"
          :y="selected ? -6 : -4"
        />
      </g>
    </template>
  </g>
</template>

<script setup lang="ts">
/**
 * This is the line that is used to show connections between elements in the net.
 */
import { computed } from "vue";

import Vector from "./Vector.js";
import QuadCurve from "./relation_distribution/QuadCurve";

import LucideIcon from "@core/ui/LucideIcon.vue";

import { useRelationStore } from "@editor/items/relation/relations.store";
import Line from "./relation_distribution/Line";

import type { Handleable } from "@core/types/Events";
import type { RelationItem } from "./RelationItem";

defineOptions({
  name: "NetRelation"
});

interface Props {
  relationId: number;
  lines?: (Line | QuadCurve)[];
  selected?: boolean;
  connecting?: boolean;
  highlighted?: boolean;
  fade?: boolean;
  incoming?: boolean;
  outgoing?: boolean;
  non?: boolean;
  endpointsOnly?: boolean;
  clickable: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  lines: () => [],
  selected: false,
  connecting: false,
  highlighted: false,
  fade: false,
  incoming: false,
  outgoing: false,
  non: false,
  endpointsOnly: false,
  clickable: true
});

const emit = defineEmits<{
  (e: "select", relation: RelationItem, event: MouseEvent): void;
  (e: "add-select", relation: RelationItem): void;
  (e: "contextmenu", event: MouseEvent, relation: RelationItem | null): void;
}>();

const relationStore = useRelationStore();

const relation = computed(() => {
  if (props.relationId == null) return null;
  return relationStore.items[props.relationId];
});

const maxInfluence = computed(() => {
  if (relation.value == null) return 0;
  return relationStore.maxInfluences[props.relationId];
});

/**
 * strength is a three-tier categorization of the max influence of the relation
 * used to show line width
 */
const strength = computed(() => {
  return relationStore.strengths[props.relationId];
});

// ===================
// RELATION STATUS

const isSlopeNegative = computed(() => {
  if (maxInfluence.value === 0) return relation.value?.inverse == true;
  return relationStore.curveSlopes[props.relationId] < 0;
});

const isFaded = computed(() => {
  if (
    props.selected ||
    props.highlighted ||
    props.incoming ||
    props.outgoing ||
    props.connecting
  )
    return false;
  return props.fade;
});

const isZero = computed(() => {
  return maxInfluence.value == 0;
});

// ===================
// SVG properties

const end = computed(() => {
  if (props.lines.length === 0) return null;
  return props.lines[props.lines.length - 1].end;
});

/**
 * SVG Path description of the total path.
 * @see https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d#path_commands
 */
const description = computed(() => {
  if (props.lines.length === 0) return "";
  let lines = props.lines;
  let description = `M${lines[0].start.x} ${lines[0].start.y}`;
  let useStartToo = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (useStartToo) {
      description += `L${line.start.x} ${line.start.y}`;
      useStartToo = false;
    }
    if (QuadCurve.getLength(line) === 0) useStartToo = true;
    else {
      if (line.type === "curve") {
        const curve = line as QuadCurve;
        description += `Q${curve.bezier.x} ${curve.bezier.y}, ${curve.end.x} ${curve.end.y}`;
      } else {
        // don't move the last line to the end
        if (i == lines.length - 1) {
          const END_MARGIN = 7;
          switch (line.direction) {
            case "down":
              description += `L${line.end.x} ${line.end.y - END_MARGIN}`;
              break;

            case "up":
              description += `L${line.end.x} ${line.end.y + END_MARGIN}`;
              break;

            case "left":
              description += `L${line.end.x + END_MARGIN} ${line.end.y}`;
              break;

            case "right":
              description += `L${line.end.x - END_MARGIN} ${line.end.y}`;
              break;
          }
        } else {
          description += `L${line.end.x} ${line.end.y}`;
        }
      }
    }
  }
  return description;
});

/**
 * SVG Path description for the arrow at the end of the line.
 * @see https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d#path_commands
 */
const arrowheadPoints = computed(() => {
  if (end.value == null) return "";
  const lastPoint = props.lines[props.lines.length - 1].start;
  const cursor = new Vector(0, 0);
  let pointString = `${cursor.x},${cursor.y} `;
  const arrowWidth = 19;
  const movement1 = new Vector(-15, 0);
  const movement2 = new Vector(0, arrowWidth / 2);
  const movement3 = new Vector(0, -arrowWidth);
  let degree = Math.PI;
  const bezier = new Vector(
    lastPoint.x - end.value.x,
    lastPoint.y - end.value.y
  );
  degree += bezier.toAngles();
  movement1.turn(degree);
  movement2.turn(degree);
  movement3.turn(degree);
  cursor.add(movement1);
  cursor.add(movement2);
  pointString += `${cursor.x},${cursor.y} `;
  cursor.add(movement3);
  pointString += `${cursor.x},${cursor.y}`;
  return pointString;
});

/**
 * Position of the first inverse mark.
 * (center of the mark)
 */
const inverseMarkCenter = computed(() => {
  // get the total length of the line
  const totalLength = props.lines.reduce(
    (sum, line) => sum + QuadCurve.getLength(line),
    0
  );
  // find the line the half point is in
  let pointPosition = 20;
  if (totalLength / 10 < pointPosition) pointPosition = totalLength / 10;
  for (const line of props.lines) {
    const lineLength = QuadCurve.getLength(line);
    // if the half is not on this line, continue.
    if (pointPosition > lineLength) {
      pointPosition -= lineLength;
      continue;
    }
    // if the half line is on this segment, get the point at the percent point
    const percent = pointPosition / lineLength;
    return {
      x: line.start.x + (line.end.x - line.start.x) * percent,
      y: line.start.y + (line.end.y - line.start.y) * percent
    };
  }
  return null;
});

/**
 * Position of the second inverse mark.
 * (center of the mark)
 */
const inverseMarkCenter2 = computed(() => {
  // get the total length of the line
  const totalLength = props.lines.reduce(
    (sum, line) => sum + QuadCurve.getLength(line),
    0
  );
  // find the line the half point is in
  let pointPosition = totalLength - 30;
  if (totalLength - totalLength / 5 > pointPosition) {
    pointPosition = totalLength - totalLength / 5;
  }
  for (const line of props.lines) {
    const lineLength = QuadCurve.getLength(line);
    // if the half is not on this line, continue.
    if (pointPosition > lineLength) {
      pointPosition -= lineLength;
      continue;
    }
    // if the half line is on this segment, get the point at the percent point
    const percent = pointPosition / lineLength;
    return {
      x: line.start.x + (line.end.x - line.start.x) * percent,
      y: line.start.y + (line.end.y - line.start.y) * percent
    };
  }
  return null;
});

const ENDPOINT_LENGTH = 20;

const lineInStartPoint = computed(() => {
  const start = props.lines[props.lines.length - 1].start;
  const end = props.lines[props.lines.length - 1].end;
  if (start.x == end.x) {
    return {
      x: start.x,
      y: clamp(start.y, end.y - ENDPOINT_LENGTH, end.y + ENDPOINT_LENGTH)
    };
  }
  if (start.y == end.y) {
    return {
      x: clamp(start.x, end.x - ENDPOINT_LENGTH, end.x + ENDPOINT_LENGTH),
      y: start.y
    };
  }
  return start;
});
const lineInDescription = computed(() => {
  const startPoint = lineInStartPoint.value;
  const end = props.lines[props.lines.length - 1].end;
  
  // cut off the last part of the line
  // to make space for the arrowhead
  let endPoint = { ...end };
  if (startPoint.x == end.x) {
    if (endPoint.y > startPoint.y) {
      endPoint.y -= 8;
    } else {
      endPoint.y += 8;
    }
  }
  if (startPoint.y == end.y) {
    if (endPoint.x > startPoint.x) {
      endPoint.x -= 8;
    } else {
      endPoint.x += 8;
    }
  }
  
  return `M${startPoint.x} ${startPoint.y}L${endPoint.x} ${endPoint.y}`;
});

const lineOutEndPoint = computed(() => {
  const start = props.lines[0].start;
  const end = props.lines[0].end;
  if (start.x == end.x) {
    return {
      x: end.x,
      y: clamp(end.y, start.y - ENDPOINT_LENGTH, start.y + ENDPOINT_LENGTH)
    };
  }
  if (start.y == end.y) {
    return {
      x: clamp(end.x, start.x - ENDPOINT_LENGTH, start.x + ENDPOINT_LENGTH),
      y: end.y
    };
  }
  return end;
});
const lineOutDescription = computed(() => {
  const start = props.lines[0].start;
  return `M${start.x} ${start.y}L${lineOutEndPoint.value.x} ${lineOutEndPoint.value.y}`;
});

function clamp(value: number, min: number, max: number) {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

// ===================
// Methods

function markAsHandled(mouseEvent: MouseEvent & Handleable) {
  mouseEvent.preventDefault();
  mouseEvent.hasBeenHandled = true;
}

/**
 * select the relation
 */
function select(mouseEvent: MouseEvent & Handleable) {
  mouseEvent.preventDefault();
  mouseEvent.hasBeenHandled = true;
  if (relation.value == null) return;
  emit("select", relation.value, mouseEvent);
}

/**
 * Emit event to add relation to selection.
 */
function addToSelection(mouseEvent: MouseEvent & Handleable) {
  mouseEvent.preventDefault();
  mouseEvent.hasBeenHandled = true;
  if (relation.value) {
    emit("add-select", relation.value);
  }
}

function onPointerDown(event: MouseEvent & Handleable) {
  if (event.button == 0 || event.button == 2) {
    event.preventDefault();
    event.hasBeenHandled = true;
  }
}

function onContextMenu(event: MouseEvent) {
  emit("contextmenu", event, relation.value);
}
</script>

<style lang="scss">
.net__connection {
  pointer-events: auto;

  --relation-color: var(--net-color-relation-standard);

  &.is-inverse {
    --relation-color: var(--net-color-relation-inverse);
  }
  &.is-incoming {
    --relation-color: var(--net-color-relation-incoming);
  }
  &.is-incoming.is-inverse {
    --relation-color: var(--net-color-relation-inverse-incoming);
  }
  &.is-outgoing {
    --relation-color: var(--net-color-relation-outgoing);
  }
  &.is-outgoing.is-inverse {
    --relation-color: var(--net-color-relation-inverse-outgoing);
  }
  &.is-connecting {
    --relation-color: var(--net-color-relation-connecting);
  }
  &.is-connecting.is-inverse {
    --relation-color: var(--net-color-relation-inverse-connecting);
  }
  &.is-selected {
    --relation-color: var(--net-color-relation-active);
  }
  &.is-selected.is-inverse {
    --relation-color: var(--net-color-relation-inverse-active);
  }
  &.is-highlighted {
    --relation-color: var(--net-color-relation-highlighted);
  }
  &.is-highlighted.is-inverse {
    --relation-color: var(--net-color-relation-inverse-highlighted);
  }
  &.is-faded {
    --relation-color: var(--net-color-relation-faded);
  }
  &.is-unclickable {
    pointer-events: none;
  }

  &__linebounds {
    stroke-width: 10px;
    stroke: transparent;
    fill: none;
  }
  &__line {
    stroke: var(--relation-color);
    fill: none;
    stroke-linejoin: round;
    stroke-linecap: round;

    --stroke-width: 1px;
    --extra-stroke-width: 0px;

    stroke-width: var(--stroke-width);

    &--fill {
      pointer-events: none;
      stroke: rgba(255, 255, 255, 0.8);
    }
  }
  .is-zero &__line {
    stroke-linecap: butt;
  }
  .-non &__line {
    stroke-linecap: round;
  }

  &_weak &__line {
    --stroke-width: 2px;
  }
  &_normal &__line {
    --stroke-width: 4px;
  }
  &_strong &__line {
    --stroke-width: 5px;
  }

  &_weak &__line--fill {
    stroke-width: 2px !important;
  }
  &_normal &__line--fill {
    stroke-width: 3px !important;
  }
  &_strong &__line--fill {
    stroke-width: 4px !important;
  }

  .is-selected &__line,
  .is-outgoing &__line,
  .is-incoming &__line {
    stroke-width: calc(var(--stroke-width) + 1px);
  }

  &__arrow {
    fill: var(--relation-color);
  }

  &_weak &__arrow {
    transform: scale(0.5);
  }
  &_normal &__arrow {
    transform: scale(1);
  }
  &_strong &__arrow {
    transform: scale(1.25);
  }

  &__inversemarker {
    &__circle {
      fill: var(--relation-color);
      transform: scale(0.6);
      transition: transform 125ms;

      .is-selected & {
        transform: scale(1);
      }
      .is-faded & {
        fill: var(--net-color-relation-faded);
      }
    }
    &__icon {
      color: var(--color-action-contrast);
    }
  }

  &:hover {
    filter: drop-shadow(0 3px 3px rgba(0, 0, 0, 0.1));
  }
}
.net__bezierhandle {
  .is-selected & {
    z-index: 10;
  }
}
</style>
